import $ from 'jquery';
import * as bootstrap from 'bootstrap';

import QuarryApi from './quarry-api';
import api from '../ajax';

/**
 * "Overview" screen (quarry/job_overview_view.html).
 * No-ops when its DOM root is absent, mirroring additional-tipping.js, so
 * this file can safely ship inside the shared `main` bundle.
 */
const QuarryOverview = () => {
	const root = document.getElementById('quarryJobOverview');

	if (!root) {
		return;
	}

	const jobId = root.getAttribute('data-job-id');

	const alertEl = document.getElementById('quarryOverviewAlert');
	const entriesListEl = document.getElementById('quarryEntriesList');
	const newEntryBtn = document.getElementById('quarryNewEntryBtn');

	const clientEl = document.getElementById('quarryOverviewClient');
	const jobNameEl = document.getElementById('quarryOverviewJobName');
	const commentsEl = document.getElementById('quarryJobComments');

	const historyListEl = document.getElementById('quarryHistoryList');

	const newEntryModalEl = document.getElementById('quarryNewEntryModal');
	const newEntryJobNameEl = document.getElementById('quarryNewEntryJobName');
	const newEntryRegoSelect = document.getElementById('quarryNewEntryRego');
	const newEntryTimeSelect = document.getElementById('quarryNewEntryTime');
	const newEntryConfirmBtn = document.getElementById('quarryNewEntryConfirm');
	const newEntryErrorEl = document.getElementById('quarryNewEntryError');

	const deleteModalEl = document.getElementById('quarryDeleteEntryModal');
	const deleteConfirmBtn = document.getElementById('quarryDeleteEntryConfirm');
	const deleteErrorEl = document.getElementById('quarryDeleteEntryError');

	let currentJob = null;
	let currentRegos = [];

	const escapeHtml = (value) => {
		return String(value === undefined || value === null ? '' : value)
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;');
	};

	const showAlert = (message) => {
		if (!alertEl) {
			return;
		}

		alertEl.textContent = message;
		alertEl.classList.remove('d-none');
	};

	// -- Entries table -------------------------------------------------

	const renderEntryRow = (entry) => {
		const deleted = entry._deleted || entry.is_deleted;
		const rowClasses = ['quarry-entry-row'];

		if (deleted) {
			rowClasses.push('quarry-entry-row--deleted');
		}

		const timeDisplay = entry.time_display || '—';

		let actionHtml = '<span class="quarry-entry-row__deleted-chip">Deleted</span>';

		if (!deleted) {
			actionHtml = entry.can_delete
				? `<button type="button" class="btn btn--danger quarry-entry-row__delete"
						data-bs-toggle="modal" data-bs-target="#quarryDeleteEntryModal"
						data-entry-id="${entry.id}"
						data-registration="${escapeHtml(entry.registration)}"
						data-time-display="${escapeHtml(timeDisplay)}"
					>Delete</button>`
				: '<span class="quarry-entry-row__office" title="Entered via the office/desktop system">Office</span>';
		}

		return `
			<div class="${rowClasses.join(' ')}" data-entry-id="${entry.id}">
				<span class="quarry-entry-row__rego">${escapeHtml(entry.registration)}</span>
				<span class="quarry-entry-row__time">${escapeHtml(timeDisplay)}</span>
				<span class="quarry-entry-row__action">${actionHtml}</span>
			</div>
		`;
	};

	const renderEntries = (entries) => {
		if (!entries.length) {
			entriesListEl.innerHTML = '<div class="quarry-overview__empty">No loads logged yet for this job.</div>';

			return;
		}

		entriesListEl.innerHTML = entries.map((entry) => renderEntryRow(entry)).join('');
	};

	// -- Job history -----------------------------------------------------

	const renderHistoryItem = (history) => {
		const fullName = `${history.user_first_name || ''} ${history.user_last_name || ''}`.trim() || 'Scope Portal';
		const detailsId = `quarryHistoryDetails${history.id}`;
		const detailsHtml = history.message
			? `
				<a class="btn btn--plain collapsed" data-bs-toggle="collapse" href="#${detailsId}" role="button" aria-expanded="false" aria-controls="${detailsId}">
					View Details
				</a>
				<div class="collapse" id="${detailsId}">
					<div class="card card-body">${escapeHtml(history.message)}</div>
				</div>
			`
			: '';

		return `
			<li class="job-history__list-item">
				<strong class="job-history__action">${escapeHtml(history.action)}.</strong>
				<div class="job-history__info">
					<span>${escapeHtml(fullName)}</span> on ${escapeHtml(history.date_verbose)} at ${escapeHtml((history.time_verbose || '').toLowerCase())}
				</div>
				${detailsHtml}
			</li>
		`;
	};

	const fetchHistory = async () => {
		const { body, error } = await api.getJobHistory(jobId, this);

		if (error || !body) {
			return;
		}

		const items = Array.isArray(body) ? body : (body.results || []);

		historyListEl.innerHTML = items.map((history) => renderHistoryItem(history)).join('');
	};

	// -- New Entry modal ---------------------------------------------------

	const populateRegoSelect = (regos) => {
		if (!newEntryRegoSelect) {
			return;
		}

		newEntryRegoSelect.innerHTML = regos
			.map((rego) => `<option value="${escapeHtml(rego.registration)}">${escapeHtml(rego.registration)}</option>`)
			.join('');
	};

	const formatTimeOption = (date) => {
		const hours = date.getHours();
		const minutes = date.getMinutes();
		const hour12 = hours % 12 === 0 ? 12 : hours % 12;
		const pad = (n) => (n < 10 ? `0${n}` : `${n}`);

		return {
			value: `${pad(hours)}:${pad(minutes)}`,
			label: `${hour12}:${pad(minutes)}${hours >= 12 ? 'pm' : 'am'}`
		};
	};

	const populateTimeSelect = () => {
		if (!newEntryTimeSelect) {
			return;
		}

		const now = new Date();
		const start = new Date(now.getTime() - (3 * 60 * 60 * 1000));
		const end = new Date(now.getTime() + (60 * 60 * 1000));
		const options = [];
		let defaultValue = null;

		for (let t = new Date(start); t <= end; t = new Date(t.getTime() + 60000)) {
			const { value, label } = formatTimeOption(t);

			options.push(`<option value="${value}">${label}</option>`);

			if (t.getHours() === now.getHours() && t.getMinutes() === now.getMinutes()) {
				defaultValue = value;
			}
		}

		newEntryTimeSelect.innerHTML = options.join('');

		if (defaultValue) {
			newEntryTimeSelect.value = defaultValue;
		}
	};

	if (newEntryModalEl) {
		newEntryModalEl.addEventListener('show.bs.modal', () => {
			newEntryErrorEl.classList.add('d-none');
			newEntryJobNameEl.textContent = currentJob ? currentJob.job_name : '';

			populateRegoSelect(currentRegos);
			populateTimeSelect();
		});

		newEntryConfirmBtn.addEventListener('click', async () => {
			if (newEntryConfirmBtn.disabled) {
				return;
			}

			const registration = newEntryRegoSelect.value;
			const time = newEntryTimeSelect.value;

			if (!registration) {
				newEntryErrorEl.textContent = 'Please select a registration number.';
				newEntryErrorEl.classList.remove('d-none');

				return;
			}

			const today = new Date();
			const [hh, mm] = time.split(':');
			const loggedAt = new Date(today.getFullYear(), today.getMonth(), today.getDate(), parseInt(hh, 10), parseInt(mm, 10), 0);
			const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
			const loggedAtIso = `${loggedAt.getFullYear()}-${pad(loggedAt.getMonth() + 1)}-${pad(loggedAt.getDate())}T${pad(loggedAt.getHours())}:${pad(loggedAt.getMinutes())}:00`;

			newEntryErrorEl.classList.add('d-none');
			newEntryConfirmBtn.setAttribute('disabled', 'disabled');
			$(newEntryConfirmBtn).children('.fa-spinner').show();

			const { body, error } = await QuarryApi.createEntry(jobId, {
				registration,
				logged_at: loggedAtIso
			}, this);

			$(newEntryConfirmBtn).children('.fa-spinner').hide();
			newEntryConfirmBtn.removeAttribute('disabled');

			if (error || !body) {
				const errorBody = error && error.responseJSON;
				const message = (errorBody && (errorBody.detail || errorBody.registration)) || 'Unable to add this entry. Please try again.';

				newEntryErrorEl.textContent = Array.isArray(message) ? message[0] : message;
				newEntryErrorEl.classList.remove('d-none');

				return;
			}

			entriesListEl.insertAdjacentHTML('beforeend', renderEntryRow(body));

			const modal = bootstrap.Modal.getInstance(newEntryModalEl);

			if (modal) {
				modal.hide();
			}
		});
	}

	// -- Delete entry modal ------------------------------------------------

	if (deleteModalEl) {
		deleteModalEl.addEventListener('show.bs.modal', (e) => {
			const button = e.relatedTarget;

			if (!button) {
				return;
			}

			deleteErrorEl.classList.add('d-none');

			document.getElementById('quarryDeleteEntryClient').textContent = currentJob ? currentJob.client : '';
			document.getElementById('quarryDeleteEntryRego').textContent = button.getAttribute('data-registration');
			document.getElementById('quarryDeleteEntryTime').textContent = button.getAttribute('data-time-display');

			deleteConfirmBtn.setAttribute('data-entry-id', button.getAttribute('data-entry-id'));
		});

		deleteConfirmBtn.addEventListener('click', async () => {
			const entryId = deleteConfirmBtn.getAttribute('data-entry-id');

			if (!entryId || deleteConfirmBtn.disabled) {
				return;
			}

			deleteErrorEl.classList.add('d-none');
			deleteConfirmBtn.setAttribute('disabled', 'disabled');
			$(deleteConfirmBtn).children('.fa-spinner').show();

			const { error, status } = await QuarryApi.deleteEntry(entryId, this);

			$(deleteConfirmBtn).children('.fa-spinner').hide();
			deleteConfirmBtn.removeAttribute('disabled');

			if (error && status !== 204) {
				deleteErrorEl.textContent = (error.responseJSON && error.responseJSON.detail) || 'Unable to delete this entry. Please try again.';
				deleteErrorEl.classList.remove('d-none');

				return;
			}

			const row = entriesListEl.querySelector(`.quarry-entry-row[data-entry-id="${entryId}"]`);

			if (row) {
				row.classList.add('quarry-entry-row--deleted');

				const actionCol = row.querySelector('.quarry-entry-row__action');

				if (actionCol) {
					actionCol.innerHTML = '<span class="quarry-entry-row__deleted-chip">Deleted</span>';
				}
			}

			const modal = bootstrap.Modal.getInstance(deleteModalEl);

			if (modal) {
				modal.hide();
			}
		});
	}

	// -- Initial load --------------------------------------------------------

	const fetchAndRender = async () => {
		const { body, error } = await QuarryApi.getJobEntries(jobId, this);

		if (error || !body) {
			showAlert('Unable to load this job. Please refresh and try again.');

			return;
		}

		currentJob = body.job;
		currentRegos = body.regos || [];

		if (clientEl && body.job) {
			clientEl.textContent = body.job.client;
		}

		if (jobNameEl && body.job) {
			jobNameEl.textContent = body.job.job_name;
		}

		if (commentsEl && body.job && body.job.comments) {
			commentsEl.textContent = body.job.comments;
		}

		renderEntries(body.entries || []);

		if (newEntryBtn) {
			newEntryBtn.removeAttribute('disabled');
		}
	};

	fetchAndRender();
	fetchHistory();
};

export default QuarryOverview;
